import { useForm } from "react-hook-form";
import CustomInput from "@components/CustomInput/CustomInput";
import { CompanyInfoFields } from "./companyInfo.types";
import regexList from "@utils/regexList";
import Modal from "@components/Modal/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { EditIcon, PencilLine, Save } from "lucide-react";
import { RootState } from "@store/reducers";
import { Cropper } from "react-cropper";
import { profileImage, uploadUrl } from "@endpoints/images.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { updateCompany } from "@endpoints/company.service";
import { useSnackbar } from "notistack";
import { setCompanies } from "@store/actions/company.action";

const CompanyInfo: React.FC = () => {
  const {enqueueSnackbar} = useSnackbar()
  const dispatch = useDispatch();
  const cropperRef = useRef(null);
  const filePickerRef = useRef(null);
  const companyDetails = useSelector(
    (state: RootState) => state.company?.companies
  );
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CompanyInfoFields>({
    defaultValues: {
      isEditing: false,
      isLoading: false,
    },
  });
  const fields = watch();

  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      setValue("file", file, { shouldDirty: true });
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          //@ts-ignore
          setValue("tempImgURL", reader.result, { shouldDirty: true });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error("Error during file upload or API calls:", error);
    }
  };

  const getCroppedImage = (e) => {
    e.preventDefault();

    if (cropperRef.current) {
      const cropperInstance = cropperRef.current.cropper;
      if (cropperInstance) {
        const croppedDataUrl = cropperInstance.getCroppedCanvas().toDataURL();
        setValue("logo", croppedDataUrl);
        setValue("tempImgURL", undefined);
      } else {
        console.error("Cropper instance is not found");
      }
    } else {
      console.error("cropperRef.current is not initialized");
    }
  };

  const handleEditOrSave = async (data: CompanyInfoFields | any) => {
    if (fields?.isEditing) {
      try {
        let payload = data;
        setIsLoading(true);
        if (data?.logo && data?.logo?.includes(";base64")) {
          const { data } = await profileImage();
          if (!data?.data || !data?.data.uploadURL) {
            throw new Error("Failed to get upload URL");
          }
          const formData = new FormData();
          const base64Data = payload?.logo?.split(";base64,").pop();
          const byteArray = Uint8Array.from(atob(base64Data), (c) =>
            c.charCodeAt(0)
          );
          const blob = new Blob([byteArray], { type: payload?.file?.type });
          formData.append("file", blob, payload?.file?.name);
          const fileResponse = await uploadUrl(data?.data.uploadURL, formData);
          delete payload["file"];
          delete payload["logo"];
          delete payload["image"];
          const picURl = fileResponse?.variants?.find(
            (x) => x.split("/").slice(-1)[0] === "public"
          );
          payload["logo"] = picURl;
        }
        payload.companyId = data?._id
        const { success, data: updatedResponse } = await updateCompany(payload);
        if(success){
          dispatch(setCompanies(updatedResponse))
          enqueueSnackbar('Details updated successfully.',{variant:'success'})
        }
        console.log("this is form data", payload);
      } catch (error) {
        enqueueSnackbar(error,{variant:'error'})
      } finally {
        setIsLoading(false);
        setValue("isEditing", false);
      }
    } else {
      setValue("isEditing", true);
    }
  };

  useEffect(() => {
    if (companyDetails) {
      Object.entries(companyDetails)?.forEach(([key, value]) => {
        setValue(key as keyof CompanyInfoFields, value);
      });
    }
  }, [companyDetails]);

  console.log("this is company details", companyDetails);

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <button
        disabled={isLoading}
        onClick={
          fields?.isEditing ? handleSubmit(handleEditOrSave) : handleEditOrSave
        }
        className="flex items-center justify-center h-8 gap-2 self-end bg-primary text-white rounded-md font-semibold text-xs px-2 hover:bg-black"
      >
        {fields?.isEditing ? (
          <Save className="w-4 h-4" color="white" />
        ) : (
          <PencilLine className="w-4 h-4" color="white" />
        )}
        {fields?.isEditing ? "Save" : "Edit"}
        {isLoading ? <LoadingAnimation /> : null}
      </button>
      <div
        className={`overflow-y-scroll no-scrollbar grid grid-cols-2 gap-5 ${
          fields?.isEditing ? "opacity-100" : "opacity-60"
        }`}
      >
        {/* Form container */}
        <div className="flex flex-col gap-4">
          {/* @ts-ignore */}
          <CustomInput
            disabled={!fields?.isEditing}
            label="Company Name"
            name="name"
            placeholder={"ABC Company"}
            control={control}
            errors={errors}
          />
          {/* @ts-ignore */}
          <CustomInput
            disabled={!fields?.isEditing}
            label="Website URL"
            name="websiteUrl"
            placeholder={"Ex: doe"}
            control={control}
            errors={errors}
          />
          {/* @ts-ignore */}
          <CustomInput
            disabled={!fields?.isEditing}
            label="Primary Contact Number"
            name="primaryContactNumber"
            placeholder={"Ex: doe"}
            control={control}
            errors={errors}
          />
          {/* @ts-ignore */}
          <CustomInput
            disabled={!fields?.isEditing}
            label="Company email address"
            name="email"
            placeholder={"Ex: doe"}
            rules={{
              required: "This field is required.",
              pattern: {
                value: regexList.email,
                message: "Please enter a valid email.",
              },
            }}
            control={control}
            errors={errors}
          />
          <div className="flex flex-col gap-4 mt-2">
            <label className="font-semibold text-charcoal text-sm">
              Company address
            </label>
            {/* @ts-ignore */}
            <CustomInput
              disabled={!fields?.isEditing}
              label="Address"
              name="address1"
              placeholder={"Ex: Project Manager"}
              control={control}
              errors={errors}
            />
            {/* @ts-ignore */}
            <CustomInput
              disabled={!fields?.isEditing}
              label={"City"}
              name="city"
              placeholder={"Ex: United Kingdom"}
              control={control}
              errors={errors}
            />
            {/* @ts-ignore */}
            <CustomInput
              disabled={!fields?.isEditing}
              label={"Country"}
              name="country"
              placeholder={"Ex: United Kingdom"}
              control={control}
              errors={errors}
            />
            {/* @ts-ignore */}
            <CustomInput
              disabled={!fields?.isEditing}
              label={"Postal Code"}
              name="zipCode"
              placeholder={"Ex: United Kingdom"}
              control={control}
              errors={errors}
            />
          </div>
        </div>
        {/* Image container */}
        <div
          className={`flex  flex-col flex-1 gap-6 px-14 pt-10 ${
            fields?.isEditing ? "" : "cursor-not-allowed"
          }`}
        >
          <div className="flex self-center rounded-full h-40 w-40 bg-white items-center justify-center border">
            {fields?.logo || companyDetails?.logo ? (
              <img
                src={fields?.logo || companyDetails?.logo}
                className="rounded-full h-40 w-40 object-fill"
              />
            ) : (
              <span className="font-montserrat font-extrabold text-5xl text-gray-200">
                LOGO
              </span>
            )}
          </div>
          <button
            disabled={!fields?.isEditing}
            type="button"
            onClick={() => filePickerRef?.current?.click()}
            className="flex  items-center border rounded-lg overflow-hidden pr-2"
          >
            <span className="text-sm text-[#6f6f6f] font-medium py-2.5 px-3 bg-[#E9EAF0]">
              PNG/JPG
            </span>
            <span className="text-xs flex font-medium px-3 text-ngray flex-1 text-left whitespace-nowrap">
              {fields?.file
                ? fields?.file?.name
                : companyDetails?.logo || "Upload logo here"}
            </span>
          </button>
          <input
            ref={filePickerRef}
            type="file"
            className="hidden"
            accept=".png, .jpg"
            onChange={handleFileChange}
          />
        </div>
      </div>
      {/* Image Cropper Modal */}
      <Modal isOpen={fields?.tempImgURL}>
        <div className="flex flex-col mt-7">
          {fields?.tempImgURL && (
            <Cropper
              src={fields?.tempImgURL}
              ref={cropperRef}
              style={{ height: 400, width: "100%" }}
              aspectRatio={1}
              viewMode={2}
              guides={false}
              minCropBoxWidth={50} // Minimum width for the crop area
              minCropBoxHeight={50} // Minimum height for the crop area
            />
          )}
          <div className="mt-10 flex justify-end">
            <button
              onClick={(e) => getCroppedImage(e)}
              className="bg-primary text-white px-6 py-2.5 rounded-md"
            >
              Crop Image
            </button>
          </div>
        </div>
        <style>
          {`
                        /* Ensure the cropper's crop box is circular */
                        .cropper-face,
                        .cropper-view-box,
                        .cropper-crop-box {
                          border-radius: 50% !important; /* Make crop area circular */
                          border: 2px solid white !important; /* Optional: Set the color of the circular crop lines */
                        }

                        .cropper-face {
                          background-color: rgba(0, 0, 0, 0.3) !important; /* Optional: Set the background color of the circular crop area */
                        }

                        .cropper-view-box {
                          border-radius: 50% !important; /* Make sure the selection area is circular */
                        }

                        .cropper-crop-box {
                          border-radius: 50% !important; /* Ensure the cropping boundary is circular */
                        }
                      `}
        </style>
      </Modal>
    </div>
  );
};

export default CompanyInfo;
